// The move of a stored value from its old name to its new name (R1676,
// brand.md §1.7).
//
// The short form of the name is `ap` (R1675). The cookie and the storage
// keys of the client carried `aq` before, and a browser of a player still
// holds values under those names: the device id of fair play, the report
// outbox and three small marks. A plain rename would drop each of them.
//
// `takeRenamed` is the one step every module calls in place of a plain
// read. It reads the new name first. When the new name holds nothing, it
// copies the value of the old name to the new name. Then it deletes the
// old name. It deletes the old name only when the new name holds a value,
// so a store that refuses the write keeps the old copy for the next load.
//
// The old names stay in this step until the owner removes it (R1676
// `## Open`).

/** A store of named text values: the browser storage or the cookie jar. */
export interface NamedStore {
  get(name: string): string | null;
  set(name: string, value: string): void;
  remove(name: string): void;
}

/**
 * The value of `name`, after a value under `oldName` moved to `name`. A
 * value under `name` wins over a value under `oldName`, and the old value
 * is then deleted. The step never throws: a store that throws answers
 * null, and a write that fails keeps the old copy and answers its value.
 */
export function takeRenamed(store: NamedStore | null, name: string, oldName: string): string | null {
  if (!store) return null;
  const read = (n: string): string | null => {
    try {
      return store.get(n);
    } catch {
      return null;
    }
  };
  let value = read(name);
  const old = read(oldName);
  if (old === null) return value;
  if (value === null) {
    try {
      store.set(name, old);
    } catch { /* a refused write: the old copy stays */ }
    // A cookie jar that blocks a write does not throw, so the step reads
    // the new name again before it deletes the old one.
    if (read(name) !== old) return old;
    value = old;
  }
  try {
    store.remove(oldName);
  } catch { /* the old copy stays, and the next load deletes it */ }
  return value;
}

/** A `Storage` as a `NamedStore`, or null where there is none. */
export function storageStore(store: Storage | null | undefined): NamedStore | null {
  if (!store) return null;
  return {
    get: (n) => store.getItem(n),
    set: (n, v) => store.setItem(n, v),
    remove: (n) => store.removeItem(n),
  };
}

/** A document's cookie jar, as the part of `Document` it reads. */
export interface CookieJar {
  cookie: string;
}

/**
 * A first-party cookie jar as a `NamedStore`. A write sets `Path=/`,
 * `SameSite=Lax`, the given life and `Secure` on an `https:` page. A
 * delete writes the name with an expired life on the same path.
 */
export function cookieStore(jar: CookieJar, maxAgeSec: number, secure: boolean): NamedStore {
  const tail = `; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`;
  return {
    get(n) {
      const hit = jar.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith(n + '='));
      return hit === undefined ? null : decodeURIComponent(hit.slice(n.length + 1));
    },
    set(n, v) {
      jar.cookie = `${n}=${encodeURIComponent(v)}; Max-Age=${maxAgeSec}${tail}`;
    },
    remove(n) {
      jar.cookie = `${n}=; Max-Age=0${tail}`;
    },
  };
}
